import { ContentComponent } from '../types/template';

export const formatGenerationTemplate = (value: string): string => {
  const templateId = /^html-template-(\d+)$/.exec(value || '');
  return templateId ? `Шаблон №${templateId[1]}` : value;
};

export const formatGenerationSiteType = (value: string): string => {
  if (value === 'landing' || value === 'single') return 'Одна страница';
  if (value === 'multipage' || value === 'multi-page' || value === 'multi') return 'Несколько страниц';
  return value;
};

/**
 * Русские названия категорий компонентов для административного интерфейса.
 */
export const COMPONENT_CATEGORY_LABELS: Record<string, string> = {
  hero: 'Hero',
  header: 'Хедер',
  navigation: 'Навигация',
  menu: 'Меню',
  about: 'About / О нас',
  services: 'Услуги',
  features: 'Преимущества',
  testimonials: 'Отзывы',
  pricing: 'Тарифы',
  gallery: 'Галерея',
  portfolio: 'Портфолио',
  blog: 'Блог',
  faq: 'FAQ',
  team: 'Команда',
  cta: 'CTA',
  contact: 'Контакты',
  footer: 'Футер',
  sidebar: 'Сайдбар',
  column: 'Колонки',
  other: 'Секции'
};

/** Ключи доступных категорий */
export const CATEGORY_KEYS = Object.keys(COMPONENT_CATEGORY_LABELS);

/**
 * Рекомендуемый порядок отображения категорий в списках и фильтрах.
 */
export const CATEGORY_ORDER = [
  'hero',
  'header',
  'navigation',
  'menu',
  'about',
  'services',
  'features',
  'testimonials',
  'pricing',
  'gallery',
  'portfolio',
  'blog',
  'faq',
  'team',
  'cta',
  'contact',
  'footer',
  'sidebar',
  'column',
  'other'
];

/**
 * Русские названия областей применения (placement scope).
 */
export const PLACEMENT_SCOPE_LABELS: Record<string, string> = {
  primary: 'Основной блок',
  column: 'Колонки',
  universal: 'Универсальный',
  unknown: 'Не указано',
  undefined: 'Не указано'
};

const USD_BALANCE_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Форматирует числовой баланс пользователя в валюту USD ($1,000.00).
 * 
 * @param value - Значение баланса
 */
export const formatBalanceUSD = (value: unknown): string => {
  const raw = typeof value === 'string' ? value.trim() : value;
  const parsed = raw === '' || raw === null || raw === undefined ? 0 : Number(raw);
  const amount = Number.isFinite(parsed) ? parsed : 0;
  return USD_BALANCE_FORMATTER.format(Object.is(amount, -0) ? 0 : amount);
};

/**
 * Возвращает локализованное название категории. 
 * Если маппинг не найден, форматирует сам ключ.
 * 
 * @param key - Технический идентификатор (slug) категории
 */
export const getCategoryLabel = (key: string): string =>
  COMPONENT_CATEGORY_LABELS[key] ||
  key
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

/**
 * Проверяет, является ли компонент "шапкой" сайта на основе его имени.
 * 
 * @param component - Объект компонента
 */
export const isHeaderComponent = (component: ContentComponent): boolean => {
  const category = (component.component_name || '').split('-')[0]?.toLowerCase();
  return category === 'header';
};

/**
 * Проверяет, относится ли компонент к навигации.
 * 
 * @param component - Объект компонента
 */
export const isNavigationComponent = (component: ContentComponent): boolean => {
  const category = (component.component_name || '').split('-')[0]?.toLowerCase();
  const area = (component.template_area_type || '').toLowerCase();
  return category === 'navigation' || area === 'navigation' || area === 'menu';
};

/**
 * Определяет ориентацию меню (вертикальная или горизонтальная) на основе кода варианта.
 * 
 * @param component - Объект компонента
 */
export const getMenuOrientation = (component: ContentComponent): 'vertical' | 'horizontal' => {
  const variant = (component.variant_code || '').toLowerCase();
  if (variant.includes('vertical') || variant.includes('sidebar') || variant.includes('burger')) {
    return 'vertical';
  }
  return 'horizontal';
};

/**
 * Пытается определить наиболее подходящий ключ категории для компонента,
 * анализируя его имя, тип области и код варианта.
 * 
 * @param component - Объект компонента
 */
export const resolveCategoryKey = (component: ContentComponent): string => {
  const nameToken = component.component_name?.split('-')[0]?.toLowerCase();
  if (nameToken && CATEGORY_KEYS.includes(nameToken)) {
    return nameToken;
  }

  const templateToken = component.template_area_type?.toLowerCase();
  if (templateToken && CATEGORY_KEYS.includes(templateToken)) {
    return templateToken;
  }

  const variantToken = component.variant_code?.split('-')[0]?.toLowerCase();
  if (variantToken && CATEGORY_KEYS.includes(variantToken)) {
    return variantToken;
  }

  return 'other';
};

/**
 * Определяет область размещения компонента (основная, колонки или универсальный).
 * 
 * @param component - Объект компонента
 */
export const resolvePlacementScope = (component: ContentComponent): 'primary' | 'column' | 'universal' | 'unknown' => {
  if (component.placement_scope === 'column' || component.placement_scope === 'universal') {
    return component.placement_scope;
  }
  if (component.placement_scope === 'primary' || !component.placement_scope) {
    return 'primary';
  }
  return 'unknown';
};

/**
 * Проверяет, опубликован ли компонент (по умолчанию true, если не задано обратное).
 * 
 * @param component - Объект компонента
 */
export const isComponentPublished = (component: ContentComponent): boolean => component.is_published !== false;

/**
 * Возвращает CSS класс для визуального индикатора статуса (pill/badge).
 * 
 * @param status - Код статуса генерации
 */
export const getStatusPillClass = (status: string): string => {
  if (status === 'completed') return 'u2-pill is-ok';
  if (status === 'failed') return 'is-bad';
  if (status === 'pending' || status === 'processing') return 'u2-pill is-warn';
  return 'u2-pill';
};

/**
 * Возвращает локализованное название статуса генерации.
 * 
 * @param status - Код статуса генерации
 */
export const getStatusText = (status: string): string => {
  switch (status) {
    case 'completed': return 'Завершена';
    case 'pending': return 'В процессе';
    case 'failed': return 'Ошибка';
    case 'processing': return 'Обработка';
    case 'deleted': return 'Удалена из истории';
    default: return status;
  }
};
